/* GFE EDITORIAL LANE MOTION PARITY 2026-08-26 - DRAFT / NOT FOR PUBLICATION */
(() => {
  const section = document.querySelector('.gfe-editorial-entertainment');
  const rail = section?.querySelector('[data-gfe-editorial-rail]');
  const cards = Array.from(section?.querySelectorAll('[data-gfe-editorial-story]') || []);
  const pages = Array.from(section?.querySelectorAll('[data-gfe-editorial-page]') || []);
  const startCard = section?.querySelector('[data-gfe-editorial-start]');
  if (!section || !rail || !cards.length || cards.length !== pages.length || !startCard) return;
  if (section.dataset.gfeLaneMotionParity === 'true') return;
  section.dataset.gfeLaneMotionParity = 'true';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 768px)');
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const mix = (from, to, progress) => from + (to - from) * progress;
  const smoothstep = (progress) => progress * progress * (3 - 2 * progress);
  const px = (value) => Math.round(value * 1000) / 1000 + 'px';
  let frame = 0;
  let resizeTimer = 0;
  let desktopReady = false;
  let surfaces = [];
  let anchors = [];
  let metrics = null;

  const activeIndex = () => Math.max(cards.findIndex((card) => card.classList.contains('is-active')), 0);
  const updateActive = (index) => {
    const safeIndex = clamp(index, 0, cards.length - 1);
    cards.forEach((card, cardIndex) => {
      const active = cardIndex === safeIndex;
      card.classList.toggle('is-active', active);
      if (active) card.setAttribute('aria-current', 'true');
      else card.removeAttribute('aria-current');
    });
    pages.forEach((page, pageIndex) => {
      const active = pageIndex === safeIndex;
      page.classList.toggle('is-active', active);
      if (active) page.setAttribute('aria-current', 'true');
      else page.removeAttribute('aria-current');
    });
  };

  const wrapSurface = (card) => {
    const surface = document.createElement('div');
    surface.className = 'gfe-editorial-entertainment__surface';
    const fragment = document.createDocumentFragment();
    while (card.firstChild) fragment.appendChild(card.firstChild);
    surface.appendChild(fragment);
    card.appendChild(surface);
    return surface;
  };

  const disableDesktop = () => {
    if (!desktopReady) return;
    surfaces.forEach((surface, index) => {
      const card = cards[index];
      while (surface.firstChild) card.insertBefore(surface.firstChild, surface);
      surface.remove();
    });
    cards.forEach((card) => {
      card.style.removeProperty('transform');
      card.style.removeProperty('opacity');
    });
    rail.classList.remove('is-desktop-morph-ready');
    rail.style.removeProperty('--gfe-editorial-slot-width');
    rail.style.removeProperty('--gfe-editorial-large-height');
    surfaces = [];
    anchors = [];
    metrics = null;
    desktopReady = false;
    rail.getBoundingClientRect();
  };

  const enableDesktop = () => {
    if (desktopReady || !desktop.matches) return desktopReady;
    const focusIndex = activeIndex();
    const largeCard = cards[focusIndex];
    const smallCard = cards.find((_, index) => index !== focusIndex);
    if (!largeCard || !smallCard) return false;
    const largeRect = largeCard.getBoundingClientRect();
    const smallRect = smallCard.getBoundingClientRect();
    const smallStyle = getComputedStyle(smallCard);
    const railStyle = getComputedStyle(rail);
    metrics = {
      viewportWidth: rail.clientWidth,
      largeWidth: largeRect.width,
      smallWidth: smallRect.width,
      largeHeight: largeRect.height,
      smallHeight: smallRect.height,
      inactiveTop: Number.parseFloat(smallStyle.marginTop) || 0,
      gap: Number.parseFloat(railStyle.columnGap || railStyle.gap) || 0,
    };
    metrics.slotWidth = (metrics.largeWidth + metrics.smallWidth) / 2;
    if (!Object.values(metrics).every(Number.isFinite) || metrics.largeWidth <= metrics.smallWidth) return false;

    surfaces = cards.map(wrapSurface);
    rail.style.setProperty('--gfe-editorial-slot-width', px(metrics.slotWidth));
    rail.style.setProperty('--gfe-editorial-large-height', px(metrics.largeHeight));
    rail.classList.add('is-desktop-morph-ready');
    rail.getBoundingClientRect();
    anchors = cards.map((card) => card.offsetLeft + card.offsetWidth / 2);
    desktopReady = true;
    return true;
  };

  const nearestIndex = () => {
    const center = rail.scrollLeft + rail.clientWidth / 2;
    const centers = desktopReady ? anchors : cards.map((card) => card.offsetLeft + card.offsetWidth / 2);
    return centers.reduce((nearest, cardCenter, index) => (
      Math.abs(cardCenter - center) < Math.abs(centers[nearest] - center) ? index : nearest
    ), 0);
  };

  const render = () => {
    frame = 0;
    const center = rail.scrollLeft + rail.clientWidth / 2;
    const nearest = nearestIndex();
    if (desktop.matches && (desktopReady || enableDesktop())) {
      const focusRange = metrics.slotWidth + metrics.gap;
      surfaces.forEach((surface, index) => {
        const distance = Math.abs(anchors[index] - center);
        const linear = clamp(1 - distance / focusRange, 0, 1);
        const progress = reducedMotion.matches ? (index === nearest ? 1 : 0) : smoothstep(linear);
        const width = mix(metrics.smallWidth, metrics.largeWidth, progress);
        const height = mix(metrics.smallHeight, metrics.largeHeight, progress);
        const top = mix(metrics.inactiveTop, 0, progress);
        surface.style.width = px(width);
        surface.style.height = px(height);
        surface.style.opacity = String(mix(0.72, 1, progress));
        surface.style.transform = 'translate3d(' + px((metrics.slotWidth - width) / 2) + ',' + px(top) + ',0)';
        surface.style.zIndex = String(Math.round(10 + progress * 10));
      });
    } else {
      const gap = Number.parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap) || 0;
      const focusRange = (cards[0]?.clientWidth || rail.clientWidth) + gap;
      cards.forEach((card, index) => {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        const linear = clamp(1 - Math.abs(cardCenter - center) / focusRange, 0, 1);
        const progress = reducedMotion.matches ? (index === nearest ? 1 : 0) : smoothstep(linear);
        card.style.transition = 'none';
        card.style.transform = 'scale(' + mix(0.965, 1, progress) + ')';
        card.style.opacity = String(mix(0.72, 1, progress));
      });
    }
    updateActive(nearest);
  };

  const scheduleRender = () => {
    if (!frame) frame = window.requestAnimationFrame(render);
  };
  const centeredLeft = (index) => {
    const cardCenter = desktopReady ? anchors[index] : cards[index].offsetLeft + cards[index].offsetWidth / 2;
    return cardCenter - rail.clientWidth / 2;
  };
  const center = (index, behavior = 'smooth') => {
    const safeIndex = clamp(index, 0, cards.length - 1);
    rail.scrollTo({ left: centeredLeft(safeIndex), behavior: reducedMotion.matches ? 'auto' : behavior });
  };

  pages.forEach((page, index) => page.addEventListener('click', () => center(index, 'smooth')));
  rail.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    center(clamp(nearestIndex() + (event.key === 'ArrowRight' ? 1 : -1), 0, cards.length - 1), 'smooth');
  });
  rail.addEventListener('scroll', scheduleRender, { passive: true });

  const rebuild = () => {
    const current = nearestIndex();
    disableDesktop();
    updateActive(current);
    cards.forEach((card) => {
      card.style.removeProperty('transition');
      card.style.removeProperty('transform');
      card.style.removeProperty('opacity');
    });
    if (desktop.matches) enableDesktop();
    center(current, 'auto');
    render();
  };
  desktop.addEventListener('change', rebuild);
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(rebuild, 120);
  });

  const positionStart = () => {
    const startIndex = cards.indexOf(startCard);
    updateActive(startIndex);
    if (desktop.matches) enableDesktop();
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        center(startIndex, 'auto');
        render();
        window.setTimeout(() => { center(startIndex, 'auto'); render(); }, 140);
      });
    });
  };
  if (document.readyState === 'complete') positionStart();
  else window.addEventListener('load', positionStart, { once: true });
})();
